import { useState, useEffect } from 'react';
import * as Sentry from '@sentry/nextjs';
import { GeoDataError } from '@/types/errors';

interface GeoDataResponse {
	country_code: string;
	city: string;
	region: string;
	region_code: string;
	security: {
		anonymous: boolean;
		proxy: boolean;
		vpn: boolean;
		tor: boolean;
		hosting: boolean;
	};
}

/**
 * Internal helper function to fetch user's geographical data
 * @returns Promise<string | null> - Returns state for US users, 'US' for non-US users or failure
 */
async function fetchGeoData(): Promise<string | null> {
	const endpoint = '/api/geo-location';

	try {
		const response = await fetch(endpoint);
		if (!response.ok) {
			throw new GeoDataError(
				`Failed to fetch geo data: ${response.status} ${response.statusText}`,
				response.status,
				undefined,
				endpoint,
			);
		}

		const geoData: GeoDataResponse = await response.json();

		if (process.env.NEXT_PUBLIC_ENVIRONMENT === 'development') {
			// Edge case for testing.
			return 'California';
		}

		if (geoData.country_code !== 'US') {
			return 'US';
		}
		return geoData.region || 'US'; // Default to US if no region is found
	} catch (error) {
		// Re-throw as GeoDataError if it's not already one
		const geoError =
			error instanceof GeoDataError
				? error
				: new GeoDataError(
						error instanceof Error ? error.message : 'Unknown geo data fetch error',
						undefined,
						undefined,
						endpoint,
					);

		Sentry.withScope((scope) => {
			scope.setTag('operation', 'geo_data_fetch');
			scope.setContext('geo_fetch_details', {
				api_endpoint: endpoint,
				error_name: geoError.name,
				error_message: geoError.message,
				http_status: geoError.status,
			});
			Sentry.captureException(geoError);
		});

		return 'US'; // Default to US if no region is found
	}
}

/**
 * Custom hook for managing geo data state based on user location
 * @param enabled - Whether to fetch geo data (static during component lifecycle)
 * @returns object with stateUSA and isLoading - Returns region for US users, "Intl" for non-US users, or null when disabled/failed/no-region
 */
export function useGeoData(enabled: boolean): { stateUSA: string | null; isLoading: boolean } {
	const [stateUSA, setStateUSA] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(enabled);

	useEffect(() => {
		if (enabled) {
			const loadGeoData = async () => {
				const result = await fetchGeoData();
				setStateUSA(result);
				setIsLoading(false);
			};

			loadGeoData();
		} else {
			setStateUSA(null);
			setIsLoading(false);
		}
		// Note: 'enabled' is intentionally not in the dependency array because it's static
		// during the component lifecycle (set by Builder.io page editor, not by server/client)
	}, []); // Empty dependency array - effect runs only once on mount

	return { stateUSA, isLoading };
}
